// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The simulation's only source of randomness.
//
// WHY NOT Math.random
// -------------------
// Scatter angles, mystery awards and kicker variance all draw from here. A replay must
// reproduce them exactly or server-side validation is meaningless, so the generator has
// to be seedable, deterministic, and identical in TypeScript and C#.
// `Math.random`, `System.Random` and `UnityEngine.Random` are all banned inside the core.
//
// WHY xoshiro128** RATHER THAN PCG-64
// -----------------------------------
// The design document originally named PCG-64. PCG's 64-bit state has no exact native
// representation in JavaScript, so it would need either BigInt (slow, and allocating on
// a hot path) or hand-rolled 64-bit arithmetic over 32-bit halves (fiddly, and a
// permanent source of subtle cross-language divergence).
//
// xoshiro128** keeps four 32-bit words. JavaScript's bitwise operators and `Math.imul`
// are exactly 32-bit, and C#'s `uint` arithmetic wraps identically, so the two
// implementations agree by construction rather than by careful emulation. It is also
// faster and statistically excellent. Recorded as a deliberate change to PH-085.
//
// AUDIO DOES NOT USE THIS. Sound variation draws from a separate generator, so a replay's
// physics is fixed while its incidental audio detail is free to vary.

/** Deterministic 32-bit generator. Identical output in TypeScript and C#. */
export class Rng {
  private s0 = 0
  private s1 = 0
  private s2 = 0
  private s3 = 0

  constructor(seed: number) {
    this.reseed(seed)
  }

  /**
   * Reset to a known state.
   *
   * The seed is expanded with SplitMix32 so that adjacent seeds (1, 2, 3 …) produce
   * well-separated streams instead of correlated ones — a real failure mode when
   * tournaments seed by round number.
   */
  reseed(seed: number): void {
    let z = seed | 0
    const next = (): number => {
      z = (z + 0x9e3779b9) | 0
      let t = z
      t = Math.imul(t ^ (t >>> 16), 0x21f0aaad)
      t = Math.imul(t ^ (t >>> 15), 0x735a2d97)
      return (t ^ (t >>> 15)) >>> 0
    }
    this.s0 = next()
    this.s1 = next()
    this.s2 = next()
    this.s3 = next()
    // An all-zero state is a fixed point of xoshiro and must never occur.
    if ((this.s0 | this.s1 | this.s2 | this.s3) === 0) this.s0 = 0x9e3779b9
  }

  /** Capture the full internal state, for replay checkpointing. */
  saveState(): readonly [number, number, number, number] {
    return [this.s0, this.s1, this.s2, this.s3]
  }

  /** Restore a captured state exactly. */
  loadState(state: readonly [number, number, number, number]): void {
    this.s0 = state[0] >>> 0
    this.s1 = state[1] >>> 0
    this.s2 = state[2] >>> 0
    this.s3 = state[3] >>> 0
  }

  /** Next raw 32-bit value, `0 .. 2^32 - 1`. */
  nextUint32(): number {
    // xoshiro128** scrambler: rotl(s1 * 5, 7) * 9
    const t5 = Math.imul(this.s1, 5)
    const rotated = ((t5 << 7) | (t5 >>> 25)) >>> 0
    const result = Math.imul(rotated, 9) >>> 0

    const t = (this.s1 << 9) >>> 0
    this.s2 = (this.s2 ^ this.s0) >>> 0
    this.s3 = (this.s3 ^ this.s1) >>> 0
    this.s1 = (this.s1 ^ this.s2) >>> 0
    this.s0 = (this.s0 ^ this.s3) >>> 0
    this.s2 = (this.s2 ^ t) >>> 0
    this.s3 = ((this.s3 << 11) | (this.s3 >>> 21)) >>> 0

    return result
  }

  /**
   * Uniform double in `[0, 1)`.
   *
   * Division by 2^32 is exact in binary64 — it only adjusts the exponent — so this
   * conversion introduces no rounding and reproduces bit for bit.
   */
  nextFloat(): number {
    return this.nextUint32() / 4294967296
  }

  /** Uniform double in `[lo, hi)`. */
  nextRange(lo: number, hi: number): number {
    return lo + (hi - lo) * this.nextFloat()
  }

  /** Symmetric double in `[-magnitude, +magnitude)`. Used for scatter. */
  nextSigned(magnitude: number): number {
    return (this.nextFloat() * 2 - 1) * magnitude
  }

  /**
   * Uniform integer in `[0, bound)` with no modulo bias.
   *
   * Lemire's rejection bound.
   *
   * **`bound` must be a positive integer no greater than 2^31 − 1**, and the ceiling is the
   * interesting half of that sentence. JavaScript would happily take 2^32 − 1 here and the C#
   * sibling cannot, because its parameter is an `int` — so a bound between the two limits would
   * be a call that works in one core and throws in the other, which is a divergence dressed up
   * as a type difference. Refusing it in both places makes the two contracts the same contract.
   */
  nextInt(bound: number): number {
    if (!Number.isInteger(bound)) throw new RangeError('Rng.nextInt: bound must be an integer')
    if (bound <= 0) throw new RangeError('Rng.nextInt: bound must be positive')
    if (bound > 2147483647) throw new RangeError('Rng.nextInt: bound must fit in a signed 32-bit integer')
    const threshold = (4294967296 - bound) % bound
    for (;;) {
      const r = this.nextUint32()
      if (r >= threshold) return r % bound
    }
  }

  /** True with probability `p`. */
  nextBool(p = 0.5): boolean {
    return this.nextFloat() < p
  }
}

/**
 * Decorrelates a game's stream from the physics stream built on the same seed.
 *
 * An arbitrary odd constant. Without it a machine loaded with seed *s* and a game started on
 * seed *s* draw the identical sequence, so the match number and the ball-search scatter would
 * be locked to the physics — reproducible, and correlated in a way nobody intended.
 */
export const GAME_STREAM_SALT = 0x5bf03635

/**
 * Create the generator for a game.
 *
 * Every game records its seed in the replay header, so validation reconstructs the exact
 * stream the player experienced — which is why the seed *policy* has to live in one place.
 * It did not: this returned `new Rng(seed)` unchanged while `Game` applied the salt itself,
 * so the documented way to make a game's generator produced a different stream from the only
 * one that was ever made, and the sole test asserted the wrapper did nothing.
 */
export function createGameRng(seed: number): Rng {
  return new Rng(seed ^ GAME_STREAM_SALT)
}
